"use client";

import { trackComponent } from "@repo/analytics";
import { dlEventConstants } from "@repo/analytics/data-layer.constants";
import BFLLink from "@repo/shared-ui/BFLLink";
import BFLTabs from "@repo/shared-ui/BFLTabs";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import {
  selectLeadStatus,
  selectLoginStatus,
  useAppSelector,
} from "@repo/web-store";
import React, { FC, useCallback, useEffect, useState } from "react";
import { handleSSORedirect } from "../../components/Demat/DematAccount";
import { redirectWithUtmParams } from "../../components/Demat/DematUtils";
import TrendingStocksCarouselPanel from "./TrendingStocksCarouselPanel";
import type {
  TrendingStocksAemConfig,
  TrendingStocksCardData,
  TrendingStocksInitialSelections,
  TrendingStocksPrimaryTab,
  TrendingStockTradeAction,
} from "./types";
import { trendingStocksTrackAnalytics } from "./utils/trendingStocksCarouselAnalytics";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";
import { getSelectionKey } from "./utils/trendingStocksHelpers";

interface TrendingStocksCarouselClientProps {
  config: TrendingStocksAemConfig;
  initialSelections: TrendingStocksInitialSelections;
  title: string;
  viewAllText: string;
  viewAllLink: string;
  openInNewTab?: boolean | string;
}

const TrendingStocksCarouselClient: FC<TrendingStocksCarouselClientProps> = ({
  config,
  initialSelections,
  title,
  viewAllText,
  viewAllLink,
  openInNewTab,
}) => {
  const [activeTab, setActiveTab] = useState<TrendingStocksPrimaryTab>("stocks");

  const loginStatus = useAppSelector(selectLoginStatus);
  const leadStatus = useAppSelector(selectLeadStatus);

  const isLoggedIn = loginStatus === "success" || loginStatus === "perpetual";
  const hasBFSLAccount = leadStatus === "ETB";
  const canShowStocksTab = isLoggedIn;
  const canShowFoTab = isLoggedIn && hasBFSLAccount;
  const onboardingUrl = isOpenInApp()
    ? (config.trade?.onboardingUrlApp ?? config.trade?.onboardingUrlWeb ?? "")
    : (config.trade?.onboardingUrlWeb ?? "");

  const tabs = [
    ...(canShowStocksTab
      ? [
          {
            label: config.tabs?.stocks ?? "Stocks",
            value: "stocks" as const,
          },
        ]
      : []),
    ...(canShowFoTab
      ? [
          {
            label: config.tabs?.fo ?? "F&O",
            value: "fo" as const,
          },
        ]
      : []),
  ];
  const panelTabs =
    tabs.length > 0
      ? tabs
      : [
          {
            label: config.tabs?.stocks ?? "Stocks",
            value: "stocks" as const,
          },
        ];

  useEffect(() => {
    if (!canShowFoTab && activeTab === "fo") {
      setActiveTab("stocks");
    }
  }, [activeTab, canShowFoTab]);

  const handleTradeClick = useCallback(
    async (action: TrendingStockTradeAction, stock: TrendingStocksCardData) => {
      if (!isLoggedIn || !hasBFSLAccount) {
        if (onboardingUrl) {
          redirectWithUtmParams(onboardingUrl, "false");
        }
        return;
      }

      const exchange = stock.exchange === 2 ? 2 : 0;
      const exchId = stock.exchId || (exchange === 2 ? "2" : "0");
      const nseToken = stock.nseToken || (exchange === 0 ? stock.token : 0);
      const bseToken = stock.bseToken || (exchange === 2 ? stock.token : 0);

      await handleSSORedirect(
        action,
        loginStatus,
        exchId,
        stock.symbol,
        nseToken,
        bseToken,
        config.trade?.headerName ?? "Bajaj Broking",
      );
    },
    [
      config.trade?.headerName,
      hasBFSLAccount,
      isLoggedIn,
      loginStatus,
      onboardingUrl,
    ],
  );

  const handleViewAllClick = useCallback(() => {
    void (async () => {
      try {
        const customerId = await getCustomerIdForAnalytics();
        const trackPayload = {
          componentName: trendingStocksTrackAnalytics.componentNameCarousel,
          sectionTitle: title,
          eventType: dlEventConstants.CLICK,
          clickType: "stocks_market_card_sku_details_link_click",
          ctaText: viewAllText,
          destinationUrl: viewAllLink,
          productCategory: trendingStocksTrackAnalytics.productCategory,
          cleverTapOverrides: {
            EP_PROPERTY_TYPE: trendingStocksTrackAnalytics.propertyType,
            EP_PRODUCT_NAME: trendingStocksTrackAnalytics.productName,
            EP_SPRINT: trendingStocksTrackAnalytics.sprint,
            EP_REFERENCE_ID: trendingStocksTrackAnalytics.referenceIdLinkClick,
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
          },
        };
        await trackComponent(trackPayload);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track trending stocks carousel view all click", error);
      }
    })();
  }, [title, viewAllLink, viewAllText]);

  return (
    <section className="flex flex-col gap-3 bg-[#1778EB0A] px-4 pt-3 pb-0 md:pt-4">
      <h2 className="text-[20px] font-medium leading-[28px] text-[#1a1a1a]">
        {title}
      </h2>

      {tabs.length > 0 && (
        <div className="border-b border-[#F2F2F2]">
          <BFLTabs
            tabs={tabs}
            selectedTab={activeTab}
            onTabChange={(value: TrendingStocksPrimaryTab) => setActiveTab(value)}
            className="border-none"
            tabClassName="max-md:pb-0 pb-2 [&>p]:font-normal [&:has(span)>p]:font-medium"
            indicatorClassName="bg-[#FF6700] h-[2px] md:h-[2px] md:bottom-0 max-md:bottom-[-1px] max-md:z-[1] max-md:rounded-none"
            paragraphClassName="max-md:pb-2 md:pb-0 text-[16px]"
            gapClassName="gap-6 max-md:items-end"
          />
        </div>
      )}

      {panelTabs.map((tab) => {
        const selectionKey = getSelectionKey(tab.value, "all_calls");
        const items = initialSelections[selectionKey]?.items ?? [];

        return (
          <TrendingStocksCarouselPanel
            key={tab.value}
            isActive={activeTab === tab.value}
            items={items}
            selectionKey={selectionKey}
            tab={tab.value}
            config={config}
            onTradeClick={handleTradeClick}
          />
        );
      })}

      <BFLLink href={viewAllLink} openInNewTab={openInNewTab}>
        <div className="max-w-fit cursor-pointer border-0 bg-transparent px-0 text-left transition-opacity duration-200 ease-linear">
          <button
            type="button"
            className="text-link-primary h-[18px] cursor-pointer font-[500] text-[14px] md:text-[16px]"
            onClick={handleViewAllClick}
          >
            {viewAllText}
          </button>
        </div>
      </BFLLink>
    </section>
  );
};

export default TrendingStocksCarouselClient;
